import {
  AutoForm,
  AutoFormDataField,
  AutoFormDataFieldDate,
  DataFieldGroup,
  pickerCurrentTime,
  useAutoFormControls
} from "@9amhealth/shared";
import styled from "@emotion/styled";
import type { ZonedDateTime } from "@internationalized/date";
import type { FC } from "react";
import React, { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { IconLockInput } from "src/constants/icons";
import leftPadNumber from "src/lib/leftPadNumber";
import translate from "src/lib/translate";
import { validateDoseDate, validateRunoutDate } from "src/lib/zod/validateDate";
import useQuestionnaireCubitInContext from "src/state/QuestionnaireCubit/useQuestionnaireCubitInContext";
import type { QuestionnaireField } from "src/state/QuestionnaireStepCubit/QuestionnaireStepCubit";
import DateTimePicker from "src/ui/components/DateTimePicker/DateTimePicker";
import { z } from "zod";

const RUNOUT_MAX_DAYS_FORWARD = 365;

const PastDateWarning = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  color: var(--color-charcoal-80, #555);
`;

const isStartOfDayBeforeToday = (date: ZonedDateTime): boolean => {
  const startOfDay = date.set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0
  });
  const startOfToday = pickerCurrentTime().set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0
  });
  return startOfDay.compare(startOfToday) < 0;
};

const MedicationDoseDateField: FC<{
  field: QuestionnaireField;
}> = (props) => {
  const medicationName = props.field.properties?.input_label ?? "";
  const isRunoutVariant = Boolean(
    props.field.properties?.medication_runout_date_field
  );
  const [, { saveValue }] = useQuestionnaireCubitInContext();
  const { setValue: setFormValue } = useFormContext();
  const [showPicker, setShowPicker] = React.useState(
    () => matchMedia("(pointer:coarse)").matches
  );
  const [isSelectedDatePast, setIsSelectedDatePast] = useState(false);

  //#region Schema and types
  const MedicationDoseDateSchema = useMemo(
    () =>
      z.object({
        medication: z.string().min(1),
        date: isRunoutVariant
          ? validateRunoutDate(pickerCurrentTime())
          : validateDoseDate(
              pickerCurrentTime() // max date: today
            )
      }),
    [isRunoutVariant]
  );

  type FormValues = z.infer<typeof MedicationDoseDateSchema>;
  //#endregion

  //#region Form initial values and controls
  const initialFormValues = useMemo<FormValues>(() => {
    return {
      medication: medicationName,
      date: pickerCurrentTime()
    };
  }, [medicationName]);

  const formatDate = (date: ZonedDateTime) => {
    return `${date.year}-${leftPadNumber(date.month)}-${leftPadNumber(date.day)}`;
  };

  // set initial form value to ensure the initial date is in the questionnaire context
  useEffect(() => {
    const dateString = formatDate(initialFormValues.date);

    saveValue(props.field.id, dateString);
    setFormValue(props.field.id, dateString);
  }, [initialFormValues.date, props.field.id, saveValue, setFormValue]);

  const handleFocusChange = () => {
    const isCoarse = matchMedia("(pointer:coarse)").matches;
    if (!isCoarse) {
      return;
    }
    setShowPicker(true);
  };

  const autoFormControls = useAutoFormControls({
    schema: MedicationDoseDateSchema,
    initialValue: initialFormValues,
    validateOnChange: true,
    onChange: (key, value, { errors }) => {
      // Sync AutoForm changes to questionnaire form only if valid
      if (key === "date" && value) {
        const dateTime = value as ZonedDateTime;
        const dateString = formatDate(dateTime);

        setIsSelectedDatePast(
          isRunoutVariant && isStartOfDayBeforeToday(dateTime)
        );

        // Check if there are any validation errors for the date field
        const hasDateErrors = errors?.date && errors.date.length > 0;

        if (!hasDateErrors) {
          saveValue(props.field.id, dateString, { triggerAutoContinue: true });
        } else {
          // Clear the value if invalid to prevent Continue button from being enabled
          saveValue(props.field.id, undefined, { triggerAutoContinue: false });
        }

        setFormValue(props.field.id, dateString);
      }
    }
  });
  //#endregion

  const doseDateLabel =
    props.field.properties?.input_placeholder ??
    translate(
      isRunoutVariant
        ? "glpContinuation.field.label.runoutDate"
        : "glpContinuation.field.label.recentDoseDate",
      { context: "application" }
    );

  const handleDateTimePickerChange = (dateTime: ZonedDateTime) => {
    autoFormControls.setValue("date", dateTime);
    const dateString = formatDate(dateTime);
    saveValue(props.field.id, dateString, { triggerAutoContinue: true });
    setFormValue(props.field.id, dateString);
    setIsSelectedDatePast(isRunoutVariant && isStartOfDayBeforeToday(dateTime));
  };

  return (
    <>
      <DateTimePicker
        show={showPicker}
        onFinish={() => setShowPicker(false)}
        onChange={handleDateTimePickerChange}
        initialDate={autoFormControls.getValues().date}
        title={doseDateLabel}
        granularity="day"
        daysBack={90}
        daysForward={isRunoutVariant ? RUNOUT_MAX_DAYS_FORWARD : undefined}
        validateDate={isRunoutVariant ? () => true : undefined}
      />

      <AutoForm {...autoFormControls.props}>
        <DataFieldGroup>
          <AutoFormDataField
            name="medication"
            label={translate("glpContinuation.field.label.medication")}
            affix={IconLockInput()}
            isReadOnly
          />

          <AutoFormDataFieldDate
            onFocus={handleFocusChange}
            maxValue={
              isRunoutVariant
                ? pickerCurrentTime().add({ days: RUNOUT_MAX_DAYS_FORWARD })
                : pickerCurrentTime()
            }
            minValue={pickerCurrentTime().subtract({ days: 90 })}
            label={doseDateLabel}
            name="date"
            granularity="day"
          />
        </DataFieldGroup>
        {isRunoutVariant && isSelectedDatePast && (
          <PastDateWarning>
            {translate("medication.runout.warning_past_date")}
          </PastDateWarning>
        )}
      </AutoForm>
    </>
  );
};

export default MedicationDoseDateField;
